import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { queryClient } from '@/queries';
import { useWsConnectionStatus } from '@/hooks/useWsConnectionStatus';
import { resolveRouteQueries } from '@/ws/fallback/resolveRouteQueries';
import { resolveCustomRefetcher } from '@/ws/fallback/resolveCustomRefetcher';
import { resolveFallbackSignature } from '@/ws/fallback/resolveFallbackSignature';
import { WsFallbackConfig } from '@/ws/fallback/types';

const DEFAULT_INTERVAL_MS = 60_000;

function refetchResolvedQueries(config: WsFallbackConfig, pathname: string) {
  const queryKeys = resolveRouteQueries(config, pathname);
  const customRefetch = resolveCustomRefetcher(config, pathname);

  return Promise.allSettled([
    ...queryKeys.map((queryKey) => queryClient.refetchQueries({ queryKey })),
    ...(customRefetch ? [customRefetch()] : []),
  ]);
}

export const useWsFallbackRefetch = (config: WsFallbackConfig) => {
  const status = useWsConnectionStatus();
  const { pathname } = useLocation();
  const resolutionSignature = resolveFallbackSignature(config, pathname);
  const refetchLatest = useRef(() => refetchResolvedQueries(config, pathname));
  refetchLatest.current = () => refetchResolvedQueries(config, pathname);

  useEffect(() => {
    if (status === 'connected') return undefined;

    refetchLatest.current();

    const intervalId = setInterval(() => refetchLatest.current(), config.intervalMs ?? DEFAULT_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [status, resolutionSignature, config]);
};
